import { forwardRef, MouseEvent, ReactNode, useMemo } from 'react';

import classNames from 'classnames';

import './Menu.scss';

import { TestMetadata } from '../types';

export interface MenuItemProps {
  /** Elements to Wrap */
  children?: ReactNode;
  /** Class name */
  className?: string;
  /** Determines if the menu item in an active state */
  isActive?: boolean;
  /** Event to fire when the menu item is clicked */
  onClick?: (e: MouseEvent<HTMLLIElement>) => void;
  /** Determines if an expandable menu item in an open state */
  isOpen?: boolean;
  /** Determines if an expandable menu item has a toggle icon */
  hasToggle?: boolean;
  /** Text label for toggle menu item */
  toggleLabel?: string | ReactNode;
  /** Text label for menu item */
  label?: string | ReactNode;
  /** JSX to be inserted into .sb-menu__menu-item__toggle-target instead of label */
  toggleLabelDetail?: ReactNode;
  /** Font Awesome icon to indicate a closed sub-menu (default: fa-chevron-down) */
  closedIcon?: string;
  // Test IDs for component sections
  testMetadata?: {
    menuItem?: TestMetadata;
    menuItemLabel?: TestMetadata;
  };
}

const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>(
  (
    {
      className,
      isActive,
      isOpen,
      hasToggle,
      closedIcon = 'fa-chevron-down',
      onClick,
      toggleLabelDetail,
      toggleLabel,
      label,
      testMetadata = {
        menuItem: { 'data-testid': 'menuItem' },
        menuItemLabel: { 'data-testid': 'menuItemLabel' },
      },
      children,
      ...otherProps
    },
    ref,
  ) => {
    const menuItemClassNames = useMemo(
      () =>
        classNames('sb-menu__menu-item', className, {
          'sb-menu__menu-item_active': isActive,
          'sb-menu__menu-item_open': isOpen,
          'sb-menu__menu-item_has-toggle': hasToggle,
        }),
      [className, isActive, isOpen, hasToggle],
    );

    return (
      <li
        {...otherProps}
        ref={ref}
        className={menuItemClassNames}
        onClick={onClick}
        {...testMetadata?.menuItem}
      >
        {hasToggle && (
          <div className="sb-menu__menu-item__toggle-target" tabIndex={0}>
            {toggleLabelDetail}
            <span {...testMetadata?.menuItemLabel}>{toggleLabel}</span>
            <i className={`fas ${closedIcon}`} />
          </div>
        )}
        {label && (
          <div
            className="sb-menu__menu-item__label"
            {...testMetadata?.menuItemLabel}
          >
            {label}
          </div>
        )}
        {children}
      </li>
    );
  },
);

MenuItem.displayName = 'MenuItem';

export default MenuItem;
